import React, { useEffect, useState } from 'react';

const notificationTypes = [
  { key: 'order_scheduled', label: '예약 주문 등록' },
  { key: 'order_executed', label: '주문 체결' },
  { key: 'order_failed', label: '주문 실패' },
  { key: 'order_cancelled', label: '주문 취소' },
  { key: 'ai_analysis_complete', label: 'AI 분석 완료' },
  { key: 'ai_recommendation', label: 'AI 추천 종목' },
  { key: 'auto_trade_triggered', label: '자동거래 조건 충족' },
  { key: 'profit_target_achieved', label: '수익목표 달성' },
  { key: 'min_trade_amount_applied', label: '최저거래액 적용' },
  { key: 'error', label: '오류 알림' }
];

const NotificationSettingsTab = ({ authenticatedFetch, user }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authenticatedFetch('/api/notification/settings');
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || '알림 설정을 불러오지 못했습니다.');
      }
    } catch (fetchError) {
      setError('알림 설정 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateSetting = (path, value) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      const keys = path.split('.');
      let cursor = updated;
      keys.slice(0, -1).forEach((key) => {
        cursor[key] = { ...(cursor[key] || {}) };
        cursor = cursor[key];
      });
      cursor[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError('');
    try {
      const response = await authenticatedFetch('/api/notification/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || '알림 설정 저장에 실패했습니다.');
      }
    } catch (saveError) {
      setError('알림 설정 저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="notification-tab">
      <div className="notification-header">
        <h2>🔔 알림 설정</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={fetchSettings} className="btn-refresh" disabled={loading}>
            🔄 새로고침
          </button>
          <button onClick={handleSave} className="btn-refresh" disabled={saving || loading}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: '10px', color: '#666', fontSize: '13px' }}>
        사용자: {user?.email || '알 수 없음'}
      </div>

      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      {loading && <p style={{ color: '#666' }}>불러오는 중...</p>}

      {settings && !loading && (
        <>
          <div style={{ marginTop: '16px', padding: '12px', border: '1px solid #e9ecef', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 10px 0' }}>이메일 설정</h4>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={settings.email?.enabled || false}
                onChange={(e) => updateSetting('email.enabled', e.target.checked)}
              />
              이메일 알림 활성화
            </label>
            <input
              type="email"
              placeholder="수신 이메일 주소"
              value={settings.email?.address || ''}
              onChange={(e) => updateSetting('email.address', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            <div style={{ marginTop: '10px' }}>
              {notificationTypes.map((type) => (
                <label key={`email-${type.key}`} style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>
                  <input
                    type="checkbox"
                    checked={settings.email?.types?.[type.key] ?? false}
                    onChange={(e) => updateSetting(`email.types.${type.key}`, e.target.checked)}
                    style={{ marginRight: '6px' }}
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '12px', border: '1px solid #e9ecef', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 10px 0' }}>텔레그램 설정</h4>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={settings.telegram?.enabled || false}
                onChange={(e) => updateSetting('telegram.enabled', e.target.checked)}
              />
              텔레그램 알림 활성화
            </label>
            <input
              type="text"
              placeholder="Bot Token"
              value={settings.telegram?.bot_token || ''}
              onChange={(e) => updateSetting('telegram.bot_token', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', marginBottom: '8px' }}
            />
            <input
              type="text"
              placeholder="Chat ID"
              value={settings.telegram?.chat_id || ''}
              onChange={(e) => updateSetting('telegram.chat_id', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            <div style={{ marginTop: '10px' }}>
              {notificationTypes.map((type) => (
                <label key={`telegram-${type.key}`} style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>
                  <input
                    type="checkbox"
                    checked={settings.telegram?.types?.[type.key] ?? false}
                    onChange={(e) => updateSetting(`telegram.types.${type.key}`, e.target.checked)}
                    style={{ marginRight: '6px' }}
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSettingsTab;
